/**
 * Operator portal rework PR 9 (migration 0213) — Paelode manages an operator company's API keys
 * (/v1/platform/operator-companies/:tenantId/api-keys). A hauler key reads ONE hauler's data, for a
 * hauler Paelode confirmed for the operator; a company-wide key (13.9, D-08, DEC-372) is tied to no
 * hauler and only reads the tank list and sends tank readings. Issuing and rotating answer the
 * secret once.
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  apiErrorReasonValue,
  useApiMutation,
  useApiOpts,
  useApiQuery,
} from '@lodex-pro/web-shared';
import type {
  IssuedOperatorApiKey,
  IssueOperatorApiKeyRequest,
  OperatorApiKeyHauler,
  OperatorApiKeysResponse,
  OperatorApiKeyView,
} from '@lodex-pro/shared/validation/operator-api-credentials';

export const OPERATOR_API_KEYS_KEY = (tenantId: string): readonly unknown[] => [
  'platform',
  'operator-api-keys',
  tenantId,
];

const base = (tenantId: string): string => `/v1/platform/operator-companies/${tenantId}/api-keys`;

export function useOperatorApiKeys(
  tenantId: string,
): ReturnType<typeof useApiQuery<OperatorApiKeysResponse>> {
  const opts = useApiOpts();
  return useApiQuery<OperatorApiKeysResponse>(opts, base(tenantId), {
    queryKey: OPERATOR_API_KEYS_KEY(tenantId),
    staleTime: 15_000,
  });
}

export function useOperatorApiKeyHaulers(
  tenantId: string,
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ haulers: OperatorApiKeyHauler[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ haulers: OperatorApiKeyHauler[] }>(opts, `${base(tenantId)}/haulers`, {
    queryKey: [...OPERATOR_API_KEYS_KEY(tenantId), 'haulers'],
    staleTime: 15_000,
    enabled,
  });
}

/** Refetch the list after any change (the live count and limit move with it). */
export function useRefreshOperatorApiKeys(tenantId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: OPERATOR_API_KEYS_KEY(tenantId) });
}

export function useIssueOperatorApiKey(
  tenantId: string,
): ReturnType<typeof useApiMutation<IssueOperatorApiKeyRequest, IssuedOperatorApiKey>> {
  const opts = useApiOpts();
  return useApiMutation<IssueOperatorApiKeyRequest, IssuedOperatorApiKey>(
    opts,
    base(tenantId),
    'POST',
  );
}

/** Rotate or revoke one key: the path carries the key id, so these take it at call time. */
export function useRotateOperatorApiKey(
  tenantId: string,
  keyId: string,
): ReturnType<typeof useApiMutation<{ overlapHours: number }, IssuedOperatorApiKey>> {
  const opts = useApiOpts();
  return useApiMutation<{ overlapHours: number }, IssuedOperatorApiKey>(
    opts,
    `${base(tenantId)}/${keyId}/rotate`,
    'POST',
  );
}

export function useRevokeOperatorApiKey(
  tenantId: string,
  keyId: string,
): ReturnType<typeof useApiMutation<{ reason: string }, { key: OperatorApiKeyView }>> {
  const opts = useApiOpts();
  return useApiMutation<{ reason: string }, { key: OperatorApiKeyView }>(
    opts,
    `${base(tenantId)}/${keyId}/revoke`,
    'POST',
  );
}

/** A refused key action, in words. */
export function apiKeyError(err: unknown): string {
  const code = typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : null;
  switch (code) {
    case 'OPERATOR_API_KEY_LIMIT_REACHED': {
      const limit = apiErrorReasonValue(err, 'key_limit');
      return limit === null
        ? 'This company is using all the API keys its plan allows. Revoke one first, or raise the limit on its plan.'
        : `This company's plan allows ${limit} live API keys and all are in use. Revoke one first, or raise the limit on its plan.`;
    }
    case 'FEATURE_NOT_ENABLED':
      return "API access (OP_API) is not on this company's plan. Turn it on in the Plan card first.";
    case 'OPERATOR_API_CREDENTIAL_OPERATOR_NOT_SERVED':
      return 'You have not confirmed this hauler for the operator. Add it in the Haulers card first.';
    case 'OPERATOR_PORTAL_NOT_LINKED':
      return 'This company is not linked to an active catalog operator.';
    case 'OPERATOR_API_CREDENTIAL_ALREADY_ROTATED':
      return 'That key was already rotated. Rotate its replacement instead.';
    case 'OPERATOR_API_CREDENTIAL_NOT_ACTIVE':
      return 'That key is revoked or expired. Issue a new one instead.';
    case 'OPERATOR_API_SCOPE_NOT_FOR_KIND':
      return 'A company-wide key can only read the tank list and send tank readings.';
    default:
      return 'That did not work. Try again.';
  }
}

/** Live = what the plan's limit counts: active, not expired, not replaced by a rotation. */
export function isLiveKey(k: OperatorApiKeyView, now: Date = new Date()): boolean {
  return (
    k.status === 'ACTIVE' &&
    k.rotatedToId === null &&
    (k.expiresAt === null || new Date(k.expiresAt) > now)
  );
}

/** "Active", "Revoked", "Replaced · stops <time>", "Expired". */
export function keyStatusLabel(k: OperatorApiKeyView, now: Date = new Date()): string {
  if (k.status === 'REVOKED') return 'Revoked';
  if (k.expiresAt !== null && new Date(k.expiresAt) <= now) return 'Expired';
  if (k.rotatedToId !== null && k.expiresAt !== null) {
    return `Replaced · stops ${new Date(k.expiresAt).toLocaleString()}`;
  }
  return 'Active';
}

/** 13.9: who a key is for — "Hauler: <name>", or "Company-wide". */
export function keyKindLabel(k: OperatorApiKeyView): string {
  return k.keyKind === 'OPERATOR' || k.haulerName === null
    ? 'Company-wide'
    : `Hauler: ${k.haulerName}`;
}

/** 13.9: the two scopes a company-wide key may hold, in words. */
export const COMPANY_KEY_SCOPE_LABELS = {
  'tanks:read': 'Read the tank list',
  'readings:write': 'Send tank readings',
} as const;

export const ISSUED_BY_LABELS: Readonly<Record<OperatorApiKeyView['issuedByActor'], string>> = {
  OPERATOR: 'the operator',
  PLATFORM: 'Paelode',
  HAULER: 'the hauler (before 0213)',
};
